import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { TicketDto } from "@app/services/api/v1/visit/dto/ticket-dto";
import { ResourceModel } from "@app/models/rest/resource";
import { ResourceRestService } from "@app/services/hal/resource-rest.service";
import { SkillProfileRestService } from "@app/services/hal/skill-profile-rest.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subject, takeUntil } from "rxjs";
import { SkillProfile } from "@app/models/rest/skill-profile";
import { VisitRequirementsService } from "@app/services/api/v1/visit/visit-requirements.service";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatFormField, MatLabel, MatError } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { DialogHeaderComponent } from "../../shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-visit-ticket-editor",
  templateUrl: "./visit-ticket-editor.component.html",
  styleUrls: ["./visit-ticket-editor.component.scss"],
  imports: [
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    MatIconButton,
    MatIcon,
    MatButton,
    MatDialogActions,
    MatProgressSpinner,
    DialogHeaderComponent
  ]
})
export class VisitTicketEditorComponent implements OnInit, OnDestroy {
  data = inject<{
    visitId: number;
  }>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<VisitTicketEditorComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private visitRequirementService = inject(VisitRequirementsService);
  private resourceService = inject(ResourceRestService);
  private skillProfileService = inject(SkillProfileRestService);
  private snackBar = inject(MatSnackBar);

  visitId: number;
  parentForm: FormGroup;
  maxTickets = 2;

  resources: ResourceModel[] = [];
  skillProfiles: SkillProfile[] = [];

  loading = false;
  saving = false;

  private unsubscribe$ = new Subject<void>();

  constructor() {
    const data = this.data;

    this.visitId = data.visitId;
    this.parentForm = this.fb.group({
      ticketFormArray: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadResourcesAndSkills();
    this.loadTickets();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get ticketFormArray(): FormArray {
    return this.parentForm.get("ticketFormArray") as FormArray;
  }

  loadResourcesAndSkills() {
    this.resourceService
      .getPage()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((page) => {
        this.resources = page.resources;
      });
    this.skillProfileService
      .getPage()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((page) => {
        this.skillProfiles = page.resources;
      });
  }

  loadTickets() {
    this.loading = true;
    this.visitRequirementService.getTicketsForVisit(this.visitId).then(
      (tickets) => {
        this.ticketFormArray.clear();

        // If no tickets are returned, create one ticket by default
        if (tickets.length === 0) {
          this.ticketFormArray.push(this.createTicketForm());
        } else {
          tickets.forEach((t) =>
            this.ticketFormArray.push(this.createTicketForm(t))
          );
        }

        this.loading = false;
      },
      (error) => {
        console.error("Failed to load visit tickets:", error);
        this.snackBar.open("Failed to load tickets.", "Close", {
          duration: 3000
        });
        // If unable to load, still ensure at least one ticket form
        if (this.ticketFormArray.length === 0) {
          this.ticketFormArray.push(this.createTicketForm());
        }
        this.loading = false;
      }
    );
  }

  createTicketForm(ticket?: TicketDto): FormGroup {
    return this.fb.group({
      ticketId: [ticket?.ticketId],
      duration: [
        ticket?.duration || 60,
        [Validators.required, Validators.min(1)]
      ],
      skillProfileId: [ticket?.skillProfileId, Validators.required]
    });
  }

  get canAddTicket(): boolean {
    return this.ticketFormArray.length < this.maxTickets;
  }

  get canRemoveTicket(): boolean {
    return this.ticketFormArray.length > 1;
  }

  addTicket() {
    if (this.canAddTicket) {
      this.ticketFormArray.push(this.createTicketForm());
    }
  }

  removeTicket(index: number) {
    // Only remove if more than one ticket exists
    if (this.canRemoveTicket) {
      this.ticketFormArray.removeAt(index);
    }
  }

  onSave() {
    if (this.parentForm.invalid) {
      this.snackBar.open(
        "Please fix validation errors before saving.",
        "Close",
        {
          duration: 3000
        }
      );
      return;
    }

    this.saving = true;
    const updatedTickets: TicketDto[] = this.ticketFormArray.value;
    this.visitRequirementService
      .updateTicketsForVisit(this.visitId, updatedTickets)
      .then(
        (returnedTickets) => {
          this.snackBar.open("Tickets updated successfully!", "Close", {
            duration: 3000
          });
          this.ticketFormArray.clear();
          returnedTickets.forEach((t) =>
            this.ticketFormArray.push(this.createTicketForm(t))
          );
          this.saving = false;
          this.dialogRef.close(returnedTickets);
        },
        (error) => {
          console.error("Failed to update tickets:", error);
          let errorMessage = "Failed to update tickets.";
          // If we detect a 500 error, show a more specific message.
          if (error && error.status === 500) {
            errorMessage =
              "A system error occurred. Please try again or contact support.";
          }
          this.snackBar.open(errorMessage, "Close", {
            duration: 5000
          });
          this.saving = false;
        }
      );
  }

  onCancel() {
    this.dialogRef.close();
  }

  getTicketsControls() {
    return this.ticketFormArray.controls;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
